export const allowedProductionCapacities = [
  { name: 'Kilogram', unit: 'kg', displayUnit: 'kg' },
  { name: 'Metric tonne', unit: 'tonne', displayUnit: 'tonne' },
  { name: 'Ton', unit: 'ton', displayUnit: 'ton' },
] as const;

export const allowedProductionCapacityUnits = allowedProductionCapacities.map(tf => tf.unit);
export type ProductionCapacityUnit = (typeof allowedProductionCapacityUnits)[number];
export const displayProductionCapacityUnitByUnit: Record<ProductionCapacityUnit, string> = {
  kg: 'kg',
  tonne: 'tonne',
  ton: 'ton',
};

export const DEFAULT_PRODUCTION_CAPACITY_UNIT: ProductionCapacityUnit = 'tonne';
export const asProductionCapacityUnitOrUndefined = (
  value?: string,
): ProductionCapacityUnit | undefined => allowedProductionCapacityUnits.find(u => u === value);
export const asProductionCapacityUnitOrDefault = (value?: string): ProductionCapacityUnit =>
  asProductionCapacityUnitOrUndefined(value) ?? DEFAULT_PRODUCTION_CAPACITY_UNIT;

const KG_PER_METRIC_TONNE = 1000;
const KG_PER_SHORT_TON = 907.18474;
const SHORT_TON_TO_TONNE = KG_PER_SHORT_TON / KG_PER_METRIC_TONNE;

export type ProductionCapacityConversion = {
  to_tonne: (input: number) => number;
  from_tonne: (input: number) => number;
};
const unitConversionsRecord: Record<ProductionCapacityUnit, ProductionCapacityConversion> = {
  kg: {
    to_tonne: v => v / KG_PER_METRIC_TONNE,
    from_tonne: v => v * KG_PER_METRIC_TONNE,
  },
  tonne: { to_tonne: v => v, from_tonne: v => v },
  ton: {
    to_tonne: v => v * SHORT_TON_TO_TONNE,
    from_tonne: v => v / SHORT_TON_TO_TONNE,
  },
};

export const toTonnes = (input: number, unit?: string) => {
  const knownUnit = asProductionCapacityUnitOrDefault(unit);
  return unitConversionsRecord[knownUnit]['to_tonne'](input);
};

export const fromTonnes = (input: number, unit?: string) => {
  const knownUnit = asProductionCapacityUnitOrDefault(unit);
  return unitConversionsRecord[knownUnit]['from_tonne'](input);
};

export const productionCapacityUnitOptions = allowedProductionCapacities.map(
  ({ name, unit, displayUnit }) => ({
    label: `${name} (${displayUnit})`,
    value: unit,
  }),
);

const productionCapacityNumberFormatter = Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
export const formatProductionCapacityNumber = (num: number) =>
  productionCapacityNumberFormatter.format(num);

export const convertProductionCapacityValue = (
  value: string | undefined,
  fromUnit: string | undefined,
  toUnit: ProductionCapacityUnit,
): string | undefined => {
  if (value === undefined || String(value).trim() === '') {
    return value;
  }
  const capacityNumber = Number(value);
  if (!Number.isFinite(capacityNumber)) {
    return value;
  }
  const from = asProductionCapacityUnitOrDefault(fromUnit);
  if (from === toUnit) {
    return String(capacityNumber);
  }
  return String(fromTonnes(toTonnes(capacityNumber, from), toUnit));
};

export const productionCapacityInProjectUnit = ({
  capacity,
  assetUnit,
  projectUnit,
}: {
  capacity: string | undefined;
  assetUnit: ProductionCapacityUnit | undefined;
  projectUnit: ProductionCapacityUnit | undefined;
}) => {
  if (!capacity) return '';
  if (!projectUnit) return '';

  const capacityNumber = Number(capacity);
  if (!Number.isFinite(capacityNumber) || capacityNumber < 0) return '';

  if (assetUnit === projectUnit) return capacity;

  const base = toTonnes(capacityNumber, assetUnit);
  const converted = fromTonnes(base, projectUnit);
  return formatProductionCapacityNumber(converted);
};
